import React from 'react';

import { getComponentClasses } from '../../utils/app';
import { Spinner } from '../Spinner';

export type IconAlignment = 'left' | 'right';
export type Type = 'submit' | 'reset' | 'button' | 'menu';

export interface Attributes {
    variations?: string[];
    iconAlignment?: IconAlignment | null;
    isDisabled?: boolean;
    isActive?: boolean;
    onClick?: (
        e:
            | React.FormEvent<HTMLFormElement>
            | React.MouseEvent<HTMLAnchorElement>
            | React.MouseEvent<HTMLButtonElement>
    ) => void;
    isSaving?: boolean;
    icon?: React.ReactNode;
    style?: React.CSSProperties;
    [key: string]: any;
}

export interface Props extends Attributes {
    href?: string | null;
    type?: Type;
    children?: React.ReactNode | React.ReactNode[];
}
/**
 * @deprecated replaced by `Button` in `@99designs/emu`
 */
export class Button extends React.Component<Props, {}> {
    static defaultProps: Partial<Props> = {
        variations: ['primary'],
        isDisabled: false,
        isActive: false,
        isSaving: false,
        onClick: () => {},
        icon: null,
        href: null,
        iconAlignment: null,
        type: 'submit',
    };

    classes() {
        const variations = this.props.variations ? this.props.variations.slice() : [];

        if (Boolean(this.props.icon) && !this.props.children) {
            variations.push('icon-no-label');
            variations.push('icon'); // NB: deprecated, kept for backwards compat
        }

        if (this.props.isSaving || this.props.iconAlignment === 'left') {
            variations.push('icon-left');
        }

        if (this.props.iconAlignment === 'right' && !this.props.isSaving) {
            variations.push('icon-right');
        }

        return getComponentClasses('button', variations);
    }

    attributes() {
        const attrs: Attributes & { disabled: boolean; 'data-button-selected'?: boolean } = {
            disabled: this.isDisabled(),
        };

        if (this.props.isActive) {
            attrs['data-button-selected'] = true;
        }

        const handledProps = [
            'onClick',
            'isDisabled',
            'isActive',
            'isSaving',
            'variations',
            'icon',
            'iconAlignment',
        ];

        // Allow for underlying button/anchor api
        Object.keys(this.props).forEach((key) => {
            if (handledProps.indexOf(key) === -1) {
                attrs[key] = this.props[key];
            }
        });

        return attrs;
    }

    isDisabled() {
        return !!(this.props.isDisabled || this.props.isSaving);
    }

    onClick = (
        e:
            | React.FormEvent<HTMLFormElement>
            | React.MouseEvent<HTMLAnchorElement>
            | React.MouseEvent<HTMLButtonElement>
    ) => {
        if (!this.isDisabled() && this.props.onClick) {
            this.props.onClick(e);
        }
    };

    icon() {
        if (this.props.isSaving) {
            return (
                <span className="button__icon">
                    <Spinner />
                </span>
            );
        } else if (this.props.icon) {
            return <span className="button__icon">{this.props.icon}</span>;
        } else {
            return null;
        }
    }

    leftIcon() {
        const hasLeftIcon = this.props.icon && this.props.iconAlignment !== 'right';

        return this.props.isSaving || hasLeftIcon ? this.icon() : null;
    }

    rightIcon() {
        const hasRightIcon = this.props.icon && this.props.iconAlignment === 'right';

        return !this.props.isSaving && hasRightIcon ? this.icon() : null;
    }

    label() {
        return this.props.children ? (
            <span className="button__label">{this.props.children}</span>
        ) : null;
    }

    render() {
        // const ButtonType = this.props.href ? 'a' : 'button';
        const props = { ...this.attributes(), onClick: this.onClick, className: this.classes() };
        const buttonInner = (
            <span className="button__inner">
                {this.leftIcon()}
                {this.label()}
                {this.rightIcon()}
            </span>
        );

        return this.props.href ? (
            <a {...props}>{buttonInner}</a>
        ) : (
            <button {...props}>{buttonInner}</button>
        );
    }
}

export default Button;
